import React, { useState, useEffect, useMemo } from 'react';
import ImageSelector from '../ui/ImageSelector';
import { createImageData } from '../../lib/thumbnails';
import HeroCard from '../ui/HeroCard';
import NavigationCard from '../layout/NavigationCard';
import PostsCard from '../posts/PostsCard';
import CinematicEffectsCard from '../features/CinematicEffectsCard';
import { usePosts } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { seoConfig } from '../../lib/seo';
import '../../styles/components/masonry-grid.css';
import '../../styles/utilities/spacing.css';
import '../../styles/utilities/typography.css';
import '../../styles/utilities/sizing.css';
import '../../styles/components/feature-cards.css';

const Home: React.FC = () => {
  useCanonicalUrl(seoConfig.home);

  // Fetch dynamic posts from Supabase
  const { data: posts, loading: postsLoading, error: postsError } = usePosts();
  
  // Filter only published and publicly visible posts with useMemo to prevent dependency issues
  const publishedPosts = useMemo(() => {
    return posts?.filter(post => post.is_published && post.public_visibility) || [];
  }, [posts]);

  // Mobile detection for layout switching
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize navigation cards to prevent unnecessary re-creation
  const navigationCards = useMemo(() => [
    {
      title: 'Features',
      description: 'Discover the powerful features that make LUT Rolls perfect for photo editing on iPhone.',
      route: '/features'
    },
    {
      title: 'About LUT Rolls',
      description: 'Learn more about LUT Rolls app and the story behind it.',
      route: '/about'
    },
    {
      title: 'LUT Gallery',
      description: 'Explore our collection of LUT filters and see the transformative power of color grading.',
      route: '/gallery'
    },
    {
      title: 'Contact',
      description: 'Get in touch with us for support, feedback, or collaboration.',
      route: '/contact'
    },
    {
      title: 'Privacy Policy',
      description: 'Read our privacy policy and learn how we protect your data and privacy.',
      route: '/privacy'
    }
  ], []);


  // Memoize hero card to prevent unnecessary re-renders
  const heroCard = useMemo(() => (
    <HeroCard
      title="LUT Rolls"
      subtitle="Free Photo Editor"
      appStoreUrl="https://apps.apple.com/ca/app/lut-rolls-pro-photo-filters/id6746622304"
    />
  ), []);

  // Memoize feature card (under hero)
  const featureCard = useMemo(() => (
    <div className="feature-card feature-card-layout">
      {/* Left: Title and text (top-aligned) */}
      <div className="feature-card-content">
        <h2>Beautify Your Photos</h2>
        <p style={{fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.5, marginBlockEnd: '0.25em'}}>
          With over 50 carefully crafted photo filters, we ensure every image finds its perfect enhancement. Each photo tells a unique story, and LUT Rolls filters reveal these stories beautifully through diverse visual expressions.
        </p>
        <p style={{fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.5, marginBlockEnd: '0.25em'}}>
          Our goal is to inspire creativity and simplify your editing experience. Whether you're a professional photographer or just capturing everyday moments, LUT Rolls offers simple tools that transform your photos in a signle tap. 
        </p>
      </div>
      {/* Right: ImageSelector with filters (image fills column) */}
      <div className="feature-card-image-container">
        <div className="feature-card-image-wrapper">
          <ImageSelector
            images={createImageData([
              {
                id: 'santorini-air',
                src: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/santorini.JPG',
                label: 'Santorini Air',
                alt: ''
              },
              {
                id: 'nairobi-lens',
                src: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/nairobi.JPG',
                label: 'Nairobi lens',
                alt: ''
              },
              {
                id: 'havana-patina',
                src: 'https://odoxckqeovvymewxfvoi.supabase.co/storage/v1/object/public/post-images/imageSelectorHome/havana.JPG',
                label: 'Havana patina',
                alt: 'Havana patina LUT filter preview'
              }
            ])}
            className="image-selector-fill"
          />
        </div>
      </div>
    </div>
  ), []);

  // Memoize second feature card with reversed layout (image on left, text on right)
  const featureCard2 = useMemo(() => (
    <CinematicEffectsCard />
  ), []);

  // Memoize third feature card with three columns
  const featureCard3 = useMemo(() => (
    <div className="feature-card feature-card-three-column">
      <div className="feature-card-column">
        <img src="/assets/icons/eye-crossed.svg" alt="No Tracking" className="feature-card-icon" />
        <h3>No tracking</h3>
        <p style={{fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.5, marginBlockStart: 0, marginBlockEnd: 0}}>
          We don't collect any personal or photo data
        </p>
      </div>
      <div className="feature-card-column">
        <img src="/assets/icons/wifi-crossed.svg" alt="Works Offline" className="feature-card-icon" />
        <h3>Works offline</h3>
        <p style={{fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.5, marginBlockStart: 0, marginBlockEnd: 0}}>
          LUT Rolls does not require internet connection
        </p>
      </div>
      <div className="feature-card-column">
        <img src="/assets/icons/like-crossed.svg" alt="Pro Quality" className="feature-card-icon" />
        <h3>No social features</h3>
        <p style={{fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.5, marginBlockStart: 0, marginBlockEnd: 0}}>
          No posts, likes or comments. Personal private space.
        </p>
      </div>
    </div>
  ), []);

  // Memoize navigation cards to prevent unnecessary re-renders
  const navigationCardComponents = useMemo(() => 
    navigationCards.map((card, index) => ({
      id: `nav-${index}`,
      component: (
        <NavigationCard
          key={`nav-${index}`}
          title={card.title}
          description={card.description}
          route={card.route}
        />
      )
    }))
  , [navigationCards]);

  // Memoize other masonry items (excluding hero for desktop)
  const otherItems = useMemo(() => {
    const items = [];

    // Add PostsCard to show all posts in one card
    if (publishedPosts.length > 0 || postsLoading) {
      items.push({
        id: 'posts-card',
        component: (
          <PostsCard
            posts={publishedPosts}
            loading={postsLoading}
            error={postsError}
          />
        )
      });
    }

    // Add navigation cards after the posts card
    items.push(...navigationCardComponents);

    return items;
  }, [postsLoading, postsError, publishedPosts, navigationCardComponents]);

  // Memoize all items for mobile (including hero and feature cards)
  const allItems = useMemo(() => [
    {
      id: 'hero',
      component: heroCard
    },
    {
      id: 'feature',
      component: featureCard
    },
    {
      id: 'feature2',
      component: featureCard2
    },
    {
      id: 'feature3',
      component: featureCard3
    },
    ...otherItems
  ], [heroCard, featureCard, featureCard2, featureCard3, otherItems]);

  // Helper function to distribute items across columns for grid layout
  const distributeItemsInColumns = useMemo(() => {
    if (isMobile || otherItems.length === 0) return [[], []];
    
    const column1: typeof otherItems = [];
    const column2: typeof otherItems = [];
    
    // Distribute items alternately between columns
    otherItems.forEach((item, index) => {
      if (index % 2 === 0) {
        column1.push(item);
      } else {
        column2.push(item);
      }
    });
    
    return [column1, column2];
  }, [otherItems, isMobile]);

  return (
    <div className="home-body">
      <main className="home-main pb-22">
        {isMobile ? (
          // Mobile: Use simple vertical layout
          <div className="mobile-cards-container">
            {allItems.map((item) => (
              <div key={item.id} className="mobile-card-wrapper">
                {item.component}
              </div>
            ))}
          </div>
        ) : (
          // Desktop/Tablet: Hero card + featured cards + CSS masonry layout
          <div className="desktop-layout">
            {/* Hero card full width at top */}
            <div className="hero-container space-hero-section">
              {heroCard}
            </div>
            
            {/* First featured card full width */}
            <div className="feature-container space-hero-section">
              {featureCard}
            </div>
            
            {/* Second featured card full width */}
            <div className="feature-container space-hero-section">
              {featureCard2}
            </div>
            
            {/* Third featured card full width */}
            <div className="feature-container space-hero-section">
              {featureCard3}
            </div>
            
            {/* CSS Grid for other cards */}
            {otherItems.length > 0 && (
              <div className="css-masonry-container">
                <div className="css-masonry-column">
                  {distributeItemsInColumns[0].map((item) => (
                    <div key={item.id} className="css-masonry-item">
                      {item.component}
                    </div>
                  ))}
                </div>
                <div className="css-masonry-column">
                  {distributeItemsInColumns[1].map((item) => (
                    <div key={item.id} className="css-masonry-item">
                      {item.component}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home; 